import { useCallback, useEffect, useRef, useState } from 'react';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks/typedHooks';
import { useLazyGetCatalogFetchItemsQuery } from 'containers/product-catalog/items/api';
import { IItemBasic } from 'containers/product-catalog/items/items.model';

export interface ItemBasicQueryParams {
  isSelected: boolean;
  taxId: number;
  categoryId: number;
  search: string;
}

export const initialQueryParamsState: ItemBasicQueryParams = {
  isSelected: null,
  taxId: null,
  categoryId: null,
  search: null
};

export interface IUsePaginatedFetchItemsBasicQuery {
  isSelected?: boolean;
  taxId?: number;
  categoryId?: number;
}

export const usePaginatedFetchItemsBasicQuery = (config: IUsePaginatedFetchItemsBasicQuery = null) => {
  const [list, setList] = useState<IItemBasic[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const [queryParams, setQueryParams] = useState<ItemBasicQueryParams>({ ...initialQueryParamsState, ...config });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const page = useRef(0);
  const [fetchItemsBasicData, listQueryState] = useLazyGetCatalogFetchItemsQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const isInitListCall: boolean = page.current === 0;

  useEffect(() => {
    if (!listQueryState?.isFetching && listQueryState.isSuccess) {
      const totalCount = listQueryState?.data?.meta?.totalCount;
      const listItems = listQueryState?.data?.data ?? [];
      const updatedList = isInitListCall ? listItems : [...list, ...listItems];
      const listItemsLeftToFetch = totalCount ? +totalCount - updatedList.length : 0;

      setHasMore(listItemsLeftToFetch > 0);
      setList(updatedList);
      if (page.current === 1) {
        setTotalListCount(totalCount ?? 0);
      }
      setIsLoading(false);
    }
  }, [listQueryState?.isFetching]);

  useEffect(() => {
    if (!listQueryState?.isFetching && listQueryState?.isError) {
      setIsError(true);
      setIsLoading(false);
      setHasMore(false);
    }
  }, [listQueryState?.isError]);

  const resetListAndLoadMore = () => {
    page.current = 0;
    setList([]);
    loadMore();
  };

  useEffect(() => {
    if (isMounted) {
      resetListAndLoadMore();
    } else {
      setIsMounted(true);
    }
  }, [queryParams]);

  const loadMore = useCallback(() => {
    setIsLoading(true);
    if (currentTeam?.id) {
      const remainingParams = queryParams;

      try {
        fetchItemsBasicData({
          teamId: currentTeam?.id,
          limit: 10,
          page: page.current,
          ...remainingParams
        }).unwrap();
      } catch (err) {
        setIsError(true);
      }
    }
    page.current = page.current + 1;
  }, [currentTeam?.id, page.current, queryParams]);

  const resetRecallQuery = () => {
    resetListAndLoadMore();
  };

  const resetQuery = () => {
    page.current = 0;
    setList([]);
    setQueryParams(initialQueryParamsState);
    setIsMounted(false);
    setIsError(false);
    setHasMore(true);
    setTotalListCount(0);
  };

  return {
    isLoading: isLoading,
    isFetching: listQueryState?.isFetching,
    totalListCount,
    list,
    isError,
    loadMore,
    resetRecallQuery,
    resetQuery,
    hasMore,
    setQueryParams,
    queryParams,
    isInitListCall,
    isLoadingSuccess: listQueryState.isSuccess,
    apiErrorCode: listQueryState?.error?.code
  };
};
